import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Send } from "lucide-react";
import { useLanguage } from "@/i18n";
import { EMAIL, WHATSAPP_DISPLAY, WHATSAPP_URL } from "@/data";

export default function Contact() {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `${t.contact.formIntro} ${name}.\n${t.contact.formEmailLine} ${email}\n\n${message}`;
    window.open(`${WHATSAPP_URL}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const inputClass =
    "w-full border border-white/10 bg-[#050714]/70 px-4 py-3.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-cyan-400/60 focus:bg-[#050714]";

  return (
    <section id="contact" data-testid="contact-section" className="relative scroll-mt-20 overflow-hidden py-24 sm:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-cyan-500/10 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <span className="font-pixel text-[10px] tracking-[0.4em] text-cyan-400">// {t.contact.label}</span>
          <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            {t.contact.title}
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{t.contact.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="flex flex-col gap-6 lg:col-span-2"
          >
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="group flex items-center gap-5 border border-white/10 bg-[#0A0D24]/70 p-6 transition-all duration-500 hover:border-cyan-400/50 hover:shadow-[0_16px_48px_-20px_rgba(0,240,255,0.4)]"
            >
              <span className="pixel-corners-sm flex h-12 w-12 shrink-0 items-center justify-center border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 transition-transform duration-300 group-hover:scale-110">
                <Mail className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="font-pixel block text-[9px] uppercase tracking-[0.3em] text-slate-500">
                  {t.contact.emailLabel}
                </span>
                <span className="mt-1 block truncate text-sm font-semibold text-white sm:text-base">
                  {EMAIL}
                </span>
              </span>
            </a>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="group flex items-center gap-5 border border-white/10 bg-[#0A0D24]/70 p-6 transition-all duration-500 hover:border-emerald-400/50 hover:shadow-[0_16px_48px_-20px_rgba(52,211,153,0.4)]"
            >
              <span className="pixel-corners-sm flex h-12 w-12 shrink-0 items-center justify-center border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 transition-transform duration-300 group-hover:scale-110">
                <MessageCircle className="h-5 w-5" />
              </span>
              <span>
                <span className="font-pixel block text-[9px] uppercase tracking-[0.3em] text-slate-500">
                  {t.contact.whatsappLabel}
                </span>
                <span className="mt-1 block text-sm font-semibold text-white sm:text-base">
                  {WHATSAPP_DISPLAY}
                </span>
              </span>
            </a>

            <div className="grid-bg relative flex flex-1 items-center justify-center overflow-hidden border border-white/10 bg-[#0A0D24]/50 p-8">
              <span className="font-pixel text-center text-xs leading-loose tracking-[0.3em] text-slate-600">
                NAVX
                <br />
                <span className="text-cyan-400/60">EST. 2026</span>
                <br />
                ACRE — BRASIL
              </span>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="border border-white/10 bg-[#0A0D24]/70 p-8 sm:p-10 lg:col-span-3"
          >
            <h3 className="font-display text-lg font-bold text-white sm:text-xl">{t.contact.formTitle}</h3>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="contact-form-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.contact.formName}
                className={inputClass}
              />
              <input
                data-testid="contact-form-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.contact.formEmail}
                className={inputClass}
              />
            </div>
            <textarea
              data-testid="contact-form-message"
              required
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t.contact.formMessage}
              className={`${inputClass} mt-5 resize-none`}
            />
            <button
              type="submit"
              data-testid="contact-form-submit"
              className="pixel-corners group mt-6 flex w-full items-center justify-center gap-3 bg-cyan-400 px-7 py-4 text-sm font-bold uppercase tracking-widest text-[#050714] transition-all duration-300 hover:bg-white hover:shadow-[0_0_36px_rgba(0,240,255,0.5)] sm:w-auto"
            >
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              {t.contact.formSubmit}
            </button>
            <p className="mt-4 text-xs text-slate-500">{t.contact.formNote}</p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
